import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { searchContent, getSearchSuggestions } from "@/services/search";
import type { SearchContentResponse, SearchPersonResult, SearchSuggestionsResponse, SuggestionItem } from "@/types/search";
const trimmed = (q: string) => q.trim();

// The backend can return the same real person multiple times (once per
// role/credit, each with a different uuid) — collapse to one card per
// distinct name, keeping the first occurrence. Falls back to name_en when
// name_fa is missing so two blank-name_fa entries don't collide.
function dedupePersonsByName(persons: SearchPersonResult[]): SearchPersonResult[] {
  const seen = new Set<string>();
  const result: SearchPersonResult[] = [];
  for (const person of persons) {
    const key = (person.name_fa || person.name_en || person.uuid || "").trim().toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(person);
  }
  return result;
}

// Same duplicate-person-by-name issue shows up in the lightweight
// suggestions endpoint — dedupe only the `type: "person"` entries here,
// since two distinct movies/series can legitimately share a title and
// shouldn't be collapsed.
function dedupeSuggestionPersons(suggestions: SuggestionItem[]): SuggestionItem[] {
  const seen = new Set<string>();
  const result: SuggestionItem[] = [];
  for (const item of suggestions) {
    if (item.type !== "person") {
      result.push(item);
      continue;
    }
    const key = (item.title_fa || item.title_en || item.uuid || "").trim().toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(item);
  }
  return result;
}

/**
 * Main search results (movies + series + persons) backing the results grid.
 * Keyed by the trimmed query so identical queries dedupe/reuse cache instead of refetching,
 * and React Query cancels the in-flight request automatically when `q` changes again
 * (queryFn receives the AbortSignal and threads it into axios).
 */
export function useSearchContent(q) {
  const $ = _c(9);
  let t0;
  if ($[0] !== q) {
    t0 = trimmed(q);
    $[0] = q;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const query = t0;
  let t1;
  let t2;
  if ($[2] !== query) {
    t1 = ["search", "content", query];
    t2 = async t3 => {
      const {
        signal
      } = t3;
      const res = await searchContent({
        q: query,
        limit: 30
      }, signal);
      if (!res.data?.persons?.length) {
        return res;
      }
      return {
        ...res,
        data: {
          ...res.data,
          persons: dedupePersonsByName(res.data.persons)
        }
      };
    };
    $[2] = query;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  const t3 = query.length > 0;
  let t4;
  if ($[5] !== t1 || $[6] !== t2 || $[7] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      staleTime: 60000,
      placeholderData: _temp
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return useQuery(t4);
}

/**
 * Lightweight autocomplete suggestions shown under the search box while typing.
 * Kept as a separate query key/cache from the main results so opening the dropdown
 * never refetches or invalidates the results grid, and vice versa.
 */
function _temp(prev) {
  return prev;
}
export function useSearchSuggestions(q, enabled) {
  const $ = _c(9);
  let t0;
  if ($[0] !== q) {
    t0 = trimmed(q);
    $[0] = q;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const query = t0;
  let t1;
  let t2;
  if ($[2] !== query) {
    t1 = ["search", "suggestions", query];
    t2 = async t3 => {
      const {
        signal
      } = t3;
      const res = await getSearchSuggestions({
        q: query,
        limit: 8
      }, signal);
      if (!res.data?.suggestions?.length) {
        return res;
      }
      return {
        ...res,
        data: {
          ...res.data,
          suggestions: dedupeSuggestionPersons(res.data.suggestions)
        }
      };
    };
    $[2] = query;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  const t3 = enabled && query.length > 0;
  let t4;
  if ($[5] !== t1 || $[6] !== t2 || $[7] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      staleTime: 30000,
      placeholderData: _temp2
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return useQuery(t4);
}
function _temp2(prev) {
  return prev;
}